import React, { Component } from "react";
import { Modal, Select, Button, Spin, Switch, Row, Col, Typography, message } from "antd";
import {
  fetchKhalesCategories,
  fetchKhalesVouchers,
  fetchKhalesVoucher,
  fetchKhalesVariant
} from "../../Graphs/Card/khalesCardsAPI";

const { Option } = Select;
const { Text } = Typography;

export default class KhalesCardModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      categories: [],
      vouchers: [],
      variants: [],
      selectedCategoryId: null,
      selectedVoucherId: null,
      selectedVariantId: null,
      selectedVariant: null,
      isEnabled: true,
      isVariantLoading: false,
    };
  }

  async componentDidMount() {
    await this.loadCategories();
    const mapping = this.props.initialMapping || this.props.mapping;
    if (mapping) {
      await this.populateInitial(mapping);
    }
  }

  lastCategoryRequestId = 0;
  lastVoucherRequestId = 0;
  lastVariantRequestId = 0;

  populateInitial = async (mapping) => {
    if (!mapping) return;
    const metadata = mapping.cached_metadata || {};
    this.setState({
      selectedCategoryId: mapping.category_id || null,
      selectedVoucherId: mapping.voucher_id || null,
      selectedVariantId: mapping.variant_id || null,
      isEnabled: mapping.enabled !== undefined ? Boolean(mapping.enabled) : (mapping.is_enabled !== false),
      selectedVariant: {
        id: mapping.variant_id,
        name: metadata.variant_name || mapping.variant_name || "",
        cost: metadata.cost !== undefined ? metadata.cost : (mapping.cached_cost != null ? mapping.cached_cost : null),
        currency: metadata.currency || mapping.cached_currency || "LYD",
        in_stock: metadata.in_stock !== undefined ? metadata.in_stock : (mapping.cached_stock != null ? mapping.cached_stock : null),
        voucher_name: metadata.voucher_name || mapping.voucher_name || "",
      }
    });

    if (mapping.category_id) {
      await this.handleCategoryChange(mapping.category_id, false);
    }
    if (mapping.voucher_id) {
      await this.handleVoucherChange(mapping.voucher_id, false);
    }
  };

  loadCategories = async (retryCount = 0) => {
    try {
      this.setState({ isLoading: true });
      const res = await fetchKhalesCategories(this.props.authToken);
      let categories = [];
      if (Array.isArray(res)) {
        categories = res;
      } else if (res && Array.isArray(res.data)) {
        categories = res.data;
      } else if (res && res.data && Array.isArray(res.data.data)) {
        categories = res.data.data;
      } else if (res && Array.isArray(res.categories)) {
        categories = res.categories;
      }

      if (categories.length > 0 || (res && (res.success || res.status === 200))) {
        this.setState({ categories });
      } else if (retryCount < 2) {
        // Automatic retry in case upstream was refreshing token
        await new Promise((r) => setTimeout(r, 600));
        return await this.loadCategories(retryCount + 1);
      } else {
        message.error((res && res.message) || "Failed to load Khales categories");
      }
    } catch (err) {
      if (retryCount < 2) {
        await new Promise((r) => setTimeout(r, 600));
        return await this.loadCategories(retryCount + 1);
      }
      message.error(err.message || "Failed to load Khales categories");
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleCategoryChange = async (categoryId, resetChildren = true) => {
    const requestId = ++this.lastCategoryRequestId;
    if (resetChildren) {
      ++this.lastVoucherRequestId;
      ++this.lastVariantRequestId;
    }
    this.setState({
      selectedCategoryId: categoryId,
      ...(resetChildren ? {
        vouchers: [],
        variants: [],
        isVariantLoading: false,
        selectedVoucherId: null,
        selectedVariantId: null,
        selectedVariant: null,
      } : {}),
      isLoading: true
    });

    try {
      const res = await fetchKhalesVouchers({ category: categoryId }, this.props.authToken);
      // Stale response guard
      if (this.lastCategoryRequestId === requestId) {
        let vouchers = [];
        if (Array.isArray(res)) {
          vouchers = res;
        } else if (res && Array.isArray(res.data)) {
          vouchers = res.data;
        } else if (res && res.data && Array.isArray(res.data.data)) {
          vouchers = res.data.data;
        } else if (res && Array.isArray(res.vouchers)) {
          vouchers = res.vouchers;
        }

        if (vouchers.length > 0 || (res && (res.success || res.status === 200))) {
          this.setState({ vouchers });
        } else if (res && !res.success) {
          message.error((res && res.message) || "Failed to load Khales vouchers");
        }
      }
    } catch (err) {
      if (this.lastCategoryRequestId === requestId) {
        message.error(err.message || "Failed to load Khales vouchers");
      }
    } finally {
      if (this.lastCategoryRequestId === requestId) {
        this.setState({ isLoading: false });
      }
    }
  };

  handleVoucherChange = async (voucherId, resetVariant = true) => {
    const requestId = ++this.lastVoucherRequestId;
    if (resetVariant) ++this.lastVariantRequestId;
    this.setState({
      selectedVoucherId: voucherId,
      ...(resetVariant ? {
        isVariantLoading: false,
        variants: [],
        selectedVariantId: null,
        selectedVariant: null,
      } : {}),
      isLoading: true
    });

    try {
      const res = await fetchKhalesVoucher(voucherId, this.props.authToken);
      // Stale response guard
      if (this.lastVoucherRequestId === requestId) {
        let variants = [];
        if (res && res.data) {
          if (Array.isArray(res.data.variants)) {
            variants = res.data.variants;
          } else if (Array.isArray(res.data)) {
            variants = res.data;
          }
        } else if (res && Array.isArray(res.variants)) {
          variants = res.variants;
        } else if (Array.isArray(res)) {
          variants = res;
        }

        if (variants.length > 0 || (res && (res.success || res.status === 200))) {
          this.setState({ variants });
        } else if (res && !res.success) {
          message.error((res && res.message) || "Failed to load Khales variants");
        }
      }
    } catch (err) {
      if (this.lastVoucherRequestId === requestId) {
        message.error(err.message || "Failed to load Khales variants");
      }
    } finally {
      if (this.lastVoucherRequestId === requestId) {
        this.setState({ isLoading: false });
      }
    }
  };

  handleVariantChange = async (variantId) => {
    const requestId = ++this.lastVariantRequestId;
    const variant = this.state.variants.find((v) => String(v.id) === String(variantId));
    this.setState({
      selectedVariantId: variantId,
      selectedVariant: null,
      isVariantLoading: Boolean(variant)
    });
    if (!variant) return;

    try {
      const res = await fetchKhalesVariant(variantId, this.props.authToken);
      if (this.lastVariantRequestId !== requestId) return;
      const variantData = (res && res.data && res.data.id !== undefined) ? res.data : (res && res.id !== undefined ? res : null);
      if (!variantData || String(variantData.id) !== String(variantId)) {
        throw new Error((res && res.message) || "Failed to load Khales variant detail");
      }
      this.setState({ selectedVariant: variantData });
    } catch (err) {
      if (this.lastVariantRequestId === requestId) {
        message.error(err.message || "Failed to load Khales variant detail");
      }
    } finally {
      if (this.lastVariantRequestId === requestId) {
        this.setState({ isVariantLoading: false });
      }
    }
  };

  handleSave = () => {
    const { selectedCategoryId, selectedVoucherId, selectedVariantId, selectedVariant, isEnabled, categories, vouchers } = this.state;
    if (this.state.isVariantLoading || !selectedVariantId || !selectedVariant || String(selectedVariant.id) !== String(selectedVariantId)) {
      message.warning("Please select a variant to map");
      return;
    }

    const categoryObj = categories.find((c) => String(c.id) === String(selectedCategoryId));
    const voucherObj = vouchers.find((v) => String(v.id) === String(selectedVoucherId));

    const mappingPayload = {
      category_id: selectedCategoryId ? String(selectedCategoryId) : null,
      category_name: categoryObj ? categoryObj.name : null,
      voucher_id: selectedVoucherId ? String(selectedVoucherId) : null,
      voucher_name: voucherObj ? voucherObj.name : (selectedVariant.voucher_name || null),
      variant_id: String(selectedVariant.id),
      variant_name: selectedVariant.name || "",
      cached_cost: selectedVariant.cost != null && selectedVariant.cost !== "" ? String(selectedVariant.cost) : null,
      cached_currency: selectedVariant.currency || "LYD",
      cached_stock: selectedVariant.in_stock != null ? selectedVariant.in_stock : null,
      enabled: Boolean(isEnabled),
      is_enabled: Boolean(isEnabled)
    };

    if (typeof this.props.onSubmit === "function") {
      this.props.onSubmit(mappingPayload);
    }
    if (typeof this.props.onClose === "function") {
      this.props.onClose();
    }
  };

  handleRemove = () => {
    if (typeof this.props.onRemove === "function") {
      this.props.onRemove();
    }
    if (typeof this.props.onClose === "function") {
      this.props.onClose();
    }
  };

  render() {
    const { isVisible, onClose } = this.props;
    const {
      isLoading,
      isVariantLoading,
      categories,
      vouchers,
      variants,
      selectedCategoryId,
      selectedVoucherId,
      selectedVariantId,
      selectedVariant,
      isEnabled
    } = this.state;

    return (
      <Modal
        title="Khales Cards Mapping"
        open={isVisible}
        getContainer={this.props.getContainer !== undefined ? this.props.getContainer : false}
        zIndex={this.props.zIndex !== undefined ? this.props.zIndex : 1100}
        onCancel={onClose}
        width={650}
        footer={[
          this.props.initialMapping && (
            <Button key="remove" danger onClick={this.handleRemove} style={{ float: "left" }}>
              Remove Mapping
            </Button>
          ),
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={this.handleSave} disabled={!selectedVariantId || !selectedVariant || isVariantLoading}>
            Attach Khales Card
          </Button>,
        ]}
      >
        <Spin spinning={isLoading || isVariantLoading}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <label style={{ fontWeight: "bold" }}>Category</label>
              {categories.length === 0 && !isLoading && (
                <Button size="small" type="link" onClick={() => this.loadCategories(0)} style={{ padding: 0 }}>
                  Reload Categories
                </Button>
              )}
            </div>
            <Select
              style={{ width: "100%" }}
              placeholder="Select Khales Category"
              value={selectedCategoryId != null ? String(selectedCategoryId) : undefined}
              onChange={(val) => this.handleCategoryChange(val)}
              showSearch
              optionFilterProp="children"
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              dropdownStyle={{ zIndex: 2000 }}
            >
              {categories.map((cat) => (
                <Option key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: "bold" }}>Voucher Product</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select Khales Voucher"
              value={selectedVoucherId != null ? String(selectedVoucherId) : undefined}
              onChange={(val) => this.handleVoucherChange(val)}
              disabled={!selectedCategoryId || vouchers.length === 0}
              showSearch
              optionFilterProp="children"
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              dropdownStyle={{ zIndex: 2000 }}
            >
              {vouchers.map((v) => (
                <Option key={v.id} value={String(v.id)}>
                  {v.name}
                </Option>
              ))}
            </Select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: "bold" }}>Variant (SKU)</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select Specific Variant"
              value={selectedVariantId != null ? String(selectedVariantId) : undefined}
              onChange={(val) => this.handleVariantChange(val)}
              disabled={!selectedVoucherId || variants.length === 0}
              showSearch
              optionFilterProp="children"
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              dropdownStyle={{ zIndex: 2000 }}
            >
              {variants.map((varItem) => (
                <Option key={varItem.id} value={String(varItem.id)}>
                  {varItem.name} {varItem.price != null ? `(${varItem.price} ${varItem.currency || "LYD"})` : ""}
                </Option>
              ))}
            </Select>
          </div>

          {selectedVariant && (
            <div
              style={{
                background: "#f9f9f9",
                border: "1px solid #e8e8e8",
                padding: 16,
                borderRadius: 6,
                marginBottom: 16
              }}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Text type="secondary">Supplier Cost:</Text>
                  <div style={{ fontSize: 16, fontWeight: "bold" }}>
                    {selectedVariant.cost != null && selectedVariant.cost !== "" ? selectedVariant.cost : "N/A"} {selectedVariant.currency || "LYD"}
                  </div>
                </Col>
                <Col span={8}>
                  <Text type="secondary">Supplier Stock:</Text>
                  <div style={{ fontSize: 16, fontWeight: "bold" }}>
                    {selectedVariant.in_stock === true ? "In stock" : selectedVariant.in_stock === false ? "Out of stock" : (selectedVariant.in_stock != null ? selectedVariant.in_stock : "N/A")}
                  </div>
                </Col>
                <Col span={8}>
                  <Text type="secondary">Active Status:</Text>
                  <div>
                    <Switch
                      checked={isEnabled}
                      onChange={(checked) => this.setState({ isEnabled: checked })}
                      checkedChildren="Enabled"
                      unCheckedChildren="Disabled"
                    />
                  </div>
                </Col>
              </Row>
              <div style={{ marginTop: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Note: Upstream supplier cost is for administrative review only. Point selling price is configured independently.
                </Text>
              </div>
            </div>
          )}
        </Spin>
      </Modal>
    );
  }
}
